import type { Atom, AtomPropertyPatch } from './models/Atom';
import type { Bond, BondPropertyPatch } from './models/Bond';
import type { HydrogenBond, HydrogenBondInput } from './models/HydrogenBond';
import type { Annotation, AnnotationInput, AnnotationUpdate } from './models/Annotation';
import type { WorkspaceGroup, GroupInput } from './models/Group';
import type { WorkspaceElement } from './models/WorkspaceElement';
import type { VisibilitySettings } from './models/VisibilitySetting';
import type { Template, TemplateId } from './models/Template';
import type { GroupInstance, Transform2D, InstanceOverrides } from './models/GroupInstance';
import type { GroupId } from './utils/compoundId';
import type { ArrowIdDef } from '@alchemiesolns/arrow-def';
import { VisibilityFlag } from '@alchemiesolns/visibility-config';
import { stableId } from '@alchemiesolns/stable-id';
import { isCompoundId, parseCompoundId, makeCompoundId } from './utils/compoundId';

/**
 * MolecularWorkspace - Map-based molecular data structure with stable IDs.
 *
 * Provides clean object-based API for manipulating molecular structures while
 * maintaining backward compatibility with config strings and AtomContainer.
 *
 * Key features:
 * - Opaque atom/bond IDs (a + stableId, b + stableId) that never change
 * - Object references in bonds/arrows (no index synchronization)
 * - Immutable API (all mutations return new workspace)
 * - Lossless serialization to/from config strings
 * - Native SVG integration with stable React keys
 */
export class MolecularWorkspace {
  /** Atoms stored by stable ID */
  private readonly atoms: Map<string, Atom>;

  /** Bonds stored by stable ID */
  private readonly bonds: Map<string, Bond>;

  /** Visibility settings by atom ID */
  private readonly visibility: VisibilitySettings;

  /** Arrow definitions (transient drawing state) - stored as IDs for stability */
  private readonly arrows: ArrowIdDef[];

  /** Hydrogen bonds stored by stable ID */
  private readonly hydrogenBonds: Map<string, HydrogenBond>;

  /** Annotations (arrows, text, symbols) stored by stable ID */
  private readonly annotations: Map<string, Annotation>;

  /** Groups (named collections of atoms) stored by stable ID */
  private readonly groups: Map<string, WorkspaceGroup>;

  /** Master template definitions referenced by group instances (PIPH-77) */
  private readonly templates: Map<string, Template>;

  /** Placed instances of templates with transforms (PIPH-77) */
  private readonly groupInstances: Map<string, GroupInstance>;

  constructor(
    atoms: Map<string, Atom> = new Map(),
    bonds: Map<string, Bond> = new Map(),
    visibility: VisibilitySettings = new Map(),
    arrows: ArrowIdDef[] = [],
    hydrogenBonds: Map<string, HydrogenBond> = new Map(),
    annotations: Map<string, Annotation> = new Map(),
    groups: Map<string, WorkspaceGroup> = new Map(),
    templates: Map<string, Template> = new Map(),
    groupInstances: Map<string, GroupInstance> = new Map(),
  ) {
    this.atoms = atoms;
    this.bonds = bonds;
    this.visibility = visibility;
    this.arrows = arrows;
    this.hydrogenBonds = hydrogenBonds;
    this.annotations = annotations;
    this.groups = groups;
    this.templates = templates;
    this.groupInstances = groupInstances;
  }

  /**
   * Create a new workspace with only the specified fields changed.
   * Threads all unchanged fields through automatically.
   */
  private _derive(overrides: {
    atoms?: Map<string, Atom>;
    bonds?: Map<string, Bond>;
    visibility?: VisibilitySettings;
    arrows?: ArrowIdDef[];
    hydrogenBonds?: Map<string, HydrogenBond>;
    annotations?: Map<string, Annotation>;
    groups?: Map<string, WorkspaceGroup>;
    templates?: Map<string, Template>;
    groupInstances?: Map<string, GroupInstance>;
  }): MolecularWorkspace {
    return new MolecularWorkspace(
      overrides.atoms ?? this.atoms,
      overrides.bonds ?? this.bonds,
      overrides.visibility ?? this.visibility,
      overrides.arrows ?? this.arrows,
      overrides.hydrogenBonds ?? this.hydrogenBonds,
      overrides.annotations ?? this.annotations,
      overrides.groups ?? this.groups,
      overrides.templates ?? this.templates,
      overrides.groupInstances ?? this.groupInstances,
    );
  }

  // ========== Accessor Methods (T021-T024, T026) ==========

  /**
   * Gets atom by ID.
   *
   * Accepts both loose IDs (e.g., "atVb3RQ") and PIPH-77 compound IDs
   * (e.g., "group_3:a_5") that address atoms inside collapsed group
   * instances. Today, compound IDs always resolve to undefined because
   * group instances do not yet exist; PIPH-77 will fill in this branch
   * with a transform-aware lookup.
   *
   * @param id - Atom ID (loose or compound)
   * @returns Atom if found, undefined otherwise
   */
  getAtom(id: string): Atom | undefined {
    // Loose atoms take priority — an expanded instance's atoms
    // may have compound-ID-shaped IDs that resolve to the loose atom.
    const loose = this.atoms.get(id);
    if (loose) return loose;
    if (isCompoundId(id)) return this._resolveInstanceAtom(id);
    return undefined;
  }

  /**
   * Gets bond by ID.
   *
   * Accepts both loose IDs (e.g., "btVb3RQ") and PIPH-77 compound IDs
   * (e.g., "group_3:b_7") that address bonds inside collapsed group
   * instances. Today, compound IDs always resolve to undefined because
   * group instances do not yet exist; PIPH-77 will fill in this branch.
   *
   * @param id - Bond ID (loose or compound)
   * @returns Bond if found, undefined otherwise
   */
  getBond(id: string): Bond | undefined {
    const loose = this.bonds.get(id);
    if (loose) return loose;
    if (isCompoundId(id)) return this._resolveInstanceBond(id);
    return undefined;
  }

  // ========== Compound-ID-aware iteration (PIPH-78) ==========
  //
  // These methods establish the forward-compatible iteration contract for
  // PIPH-77 (collapsed group rendering). Today they yield only loose atoms
  // and bonds. When PIPH-77 lands, they will additionally yield compound-ID
  // atoms and bonds resolved from group instances (e.g., "group_3:a_5"),
  // without any call-site changes required.

  /**
   * Returns the total number of atoms in the workspace.
   *
   * Today this is just the loose atom count. In PIPH-77 it will include
   * atoms from collapsed group instances.
   *
   * @returns Total atom count
   */
  getAtomCount(): number {
    return this.atoms.size;
  }

  /**
   * Returns the total number of bonds in the workspace.
   *
   * Today this is just the loose bond count. In PIPH-77 it will include
   * bonds from collapsed group instances.
   *
   * @returns Total bond count
   */
  getBondCount(): number {
    return this.bonds.size;
  }

  /**
   * Returns all atom IDs in the workspace.
   *
   * Today yields only loose atom IDs. In PIPH-77 will also yield compound
   * IDs (e.g., "group_3:a_5") for atoms inside collapsed group instances.
   *
   * Order of returned IDs is unspecified — do not rely on it.
   *
   * @returns Array of atom IDs
   */
  getAtomIds(): string[] {
    return Array.from(this.atoms.keys());
  }

  /**
   * Returns all bond IDs in the workspace.
   *
   * Today yields only loose bond IDs. In PIPH-77 will also yield compound
   * IDs (e.g., "group_3:b_7") for bonds inside collapsed group instances.
   *
   * Order of returned IDs is unspecified — do not rely on it.
   *
   * @returns Array of bond IDs
   */
  getBondIds(): string[] {
    return Array.from(this.bonds.keys());
  }

  /**
   * Returns all atoms in the workspace.
   *
   * Today yields only loose atoms. In PIPH-77 will also yield atoms resolved
   * from collapsed group instances, with their positions transformed into
   * world coordinates by the instance's transform.
   *
   * Order of returned atoms is unspecified — do not rely on it.
   *
   * @returns Array of Atom objects
   */
  getAllAtoms(): Atom[] {
    return Array.from(this.atoms.values());
  }

  /**
   * Returns all bonds in the workspace.
   *
   * Today yields only loose bonds. In PIPH-77 will also yield bonds resolved
   * from collapsed group instances, with their endpoints expressed as
   * compound IDs.
   *
   * Order of returned bonds is unspecified — do not rely on it.
   *
   * @returns Array of Bond objects
   */
  getAllBonds(): Bond[] {
    return Array.from(this.bonds.values());
  }

  /**
   * Gets visibility setting for atom.
   *
   * Accepts both loose IDs and PIPH-77 compound IDs. Today, compound IDs
   * always return the default `Visible` flag because group instances do
   * not yet exist. PIPH-77 will resolve visibility through the group
   * instance's per-instance `viewOverrides.visibility` map (falling back
   * to `Visible` when no override is set).
   *
   * @param atomId - Atom ID (loose or compound)
   * @returns VisibilityFlag (defaults to Visible if not set)
   */
  getVisibility(atomId: string): VisibilityFlag {
    // Loose atoms take priority (expanded instance atoms may have compound-ID-shaped IDs)
    const looseVis = this.visibility.get(atomId);
    if (looseVis !== undefined) return looseVis;
    if (this.atoms.has(atomId)) return VisibilityFlag.Visible; // loose atom, no vis set = Visible

    if (isCompoundId(atomId)) {
      const { groupId, localId } = parseCompoundId(atomId);
      if (!groupId) return VisibilityFlag.Visible;
      const instance = this.groupInstances.get(groupId);
      if (!instance) return VisibilityFlag.Visible;
      const template = this.templates.get(instance.templateId);
      if (!template) return VisibilityFlag.Visible;
      return template.workspace.getVisibility(localId);
    }
    return VisibilityFlag.Visible;
  }

  /**
   * Gets arrow definitions as ID-based arrows.
   * @returns Array of arrow ID definitions (read-only)
   */
  getArrows(): readonly ArrowIdDef[] {
    return this.arrows;
  }

  // ========== Hydrogen Bond Accessor Methods ==========

  /**
   * Returns all hydrogen bonds in the workspace.
   * @returns Array of HydrogenBond objects
   */
  getHydrogenBonds(): HydrogenBond[] {
    return Array.from(this.hydrogenBonds.values());
  }

  /**
   * Returns a specific hydrogen bond by ID, or undefined if not found.
   * @param id - Hydrogen bond ID
   * @returns HydrogenBond if found, undefined otherwise
   */
  getHydrogenBond(id: string): HydrogenBond | undefined {
    return this.hydrogenBonds.get(id);
  }

  /**
   * Returns all hydrogen bonds involving a specific atom (as donor or acceptor).
   * @param atomId - Atom ID to search for
   * @returns Array of HydrogenBond objects involving this atom
   */
  getHydrogenBondsForAtom(atomId: string): HydrogenBond[] {
    return Array.from(this.hydrogenBonds.values()).filter(
      (hb) =>
        hb.donorAtomId === atomId ||
        hb.donorParentAtomId === atomId ||
        hb.acceptorAtomId === atomId
    );
  }

  // ========== Hydrogen Bond Mutation Methods ==========

  /**
   * Creates a new workspace with the hydrogen bond added.
   * Validates that donor/acceptor atoms exist and have valid types.
   *
   * @param input - Hydrogen bond data (id optional - stableId() generates if omitted)
   * @returns New MolecularWorkspace instance
   * @throws Error if donor/acceptor atoms don't exist or are invalid types
   */
  addHydrogenBond(input: HydrogenBondInput): MolecularWorkspace {
    // Validate atoms exist
    const donorAtom = this.atoms.get(input.donorAtomId);
    if (!donorAtom) {
      throw new Error(`Donor atom ${input.donorAtomId} not found`);
    }

    const donorParentAtom = this.atoms.get(input.donorParentAtomId);
    if (!donorParentAtom) {
      throw new Error(`Donor parent atom ${input.donorParentAtomId} not found`);
    }

    const acceptorAtom = this.atoms.get(input.acceptorAtomId);
    if (!acceptorAtom) {
      throw new Error(`Acceptor atom ${input.acceptorAtomId} not found`);
    }

    // Validate atom types
    if (donorAtom.symbol !== 'H') {
      throw new Error('Donor must be a hydrogen atom');
    }

    const validElectronegativeAtoms = ['N', 'O', 'F'];
    if (!validElectronegativeAtoms.includes(donorParentAtom.symbol)) {
      throw new Error('Donor parent must be N, O, or F');
    }

    if (!validElectronegativeAtoms.includes(acceptorAtom.symbol)) {
      throw new Error('Acceptor must be N, O, or F');
    }

    // Generate ID if not provided
    const id = input.id ?? `hb${stableId()}`;

    const newHydrogenBond: HydrogenBond = {
      id,
      donorAtomId: input.donorAtomId,
      donorParentAtomId: input.donorParentAtomId,
      acceptorAtomId: input.acceptorAtomId,
    };

    const newHydrogenBonds = new Map(this.hydrogenBonds);
    newHydrogenBonds.set(id, newHydrogenBond);

    return this._derive({ hydrogenBonds: newHydrogenBonds });
  }

  /**
   * Creates a new workspace with the hydrogen bond removed.
   *
   * @param id - ID of hydrogen bond to remove
   * @returns New MolecularWorkspace instance (unchanged if ID not found)
   */
  removeHydrogenBond(id: string): MolecularWorkspace {
    if (!this.hydrogenBonds.has(id)) {
      return this; // H-bond doesn't exist, return unchanged
    }

    const newHydrogenBonds = new Map(this.hydrogenBonds);
    newHydrogenBonds.delete(id);

    return this._derive({ hydrogenBonds: newHydrogenBonds });
  }

  /**
   * Creates a new workspace with all hydrogen bonds cleared.
   *
   * @returns New MolecularWorkspace instance
   */
  clearHydrogenBonds(): MolecularWorkspace {
    if (this.hydrogenBonds.size === 0) {
      return this; // Already empty, return unchanged
    }

    return this._derive({ hydrogenBonds: new Map() });
  }

  // ========== Annotation Methods ==========

  /**
   * Gets all annotations.
   * @returns Array of Annotation objects
   */
  getAnnotations(): Annotation[] {
    return Array.from(this.annotations.values());
  }

  /**
   * Gets annotation by ID.
   * @returns Annotation if found, undefined otherwise
   */
  getAnnotation(id: string): Annotation | undefined {
    return this.annotations.get(id);
  }

  /**
   * Creates a new workspace with the annotation added.
   * No validation against atoms/bonds — annotations are free-floating in v1.
   *
   * @param input - Annotation data (id, rotation, scale optional)
   * @returns Object with new workspace and the generated annotation ID
   */
  addAnnotation(input: AnnotationInput): { workspace: MolecularWorkspace; id: string } {
    const id = input.id ?? `an${stableId()}`;

    let annotation: Annotation;
    if (input.type === 'symbol') {
      annotation = {
        type: 'symbol',
        id,
        x: input.x,
        y: input.y,
        rotation: input.rotation ?? 0,
        scale: input.scale ?? 1,
        symbolId: input.symbolId,
        shaftLength: input.shaftLength,
      };
    } else {
      annotation = {
        type: 'text',
        id,
        x: input.x,
        y: input.y,
        rotation: input.rotation ?? 0,
        scale: input.scale ?? 1,
        text: input.text,
        fontSize: input.fontSize,
        alignment: input.alignment,
      };
    }

    const newAnnotations = new Map(this.annotations);
    newAnnotations.set(id, annotation);

    const workspace = this._derive({ annotations: newAnnotations });
    return { workspace, id };
  }

  /**
   * Creates a new workspace with the annotation removed.
   *
   * @param id - ID of annotation to remove
   * @returns New MolecularWorkspace instance (unchanged if ID not found)
   */
  removeAnnotation(id: string): MolecularWorkspace {
    if (!this.annotations.has(id)) {
      return this;
    }

    const newAnnotations = new Map(this.annotations);
    newAnnotations.delete(id);

    return this._derive({ annotations: newAnnotations });
  }

  /**
   * Creates a new workspace with the annotation's base properties updated.
   * Only AnnotationBase fields (x, y, rotation, scale) can be updated.
   *
   * @param id - ID of annotation to update
   * @param updates - Partial AnnotationBase fields to merge
   * @returns New MolecularWorkspace instance (unchanged if ID not found)
   */
  updateAnnotation(id: string, updates: AnnotationUpdate): MolecularWorkspace {
    const existing = this.annotations.get(id);
    if (!existing) {
      return this;
    }

    const updated: Annotation = { ...existing, ...updates } as Annotation;

    const newAnnotations = new Map(this.annotations);
    newAnnotations.set(id, updated);

    return this._derive({ annotations: newAnnotations });
  }

  /**
   * Creates a new workspace with all annotations cleared.
   *
   * @returns New MolecularWorkspace instance
   */
  clearAnnotations(): MolecularWorkspace {
    if (this.annotations.size === 0) {
      return this;
    }

    return this._derive({ annotations: new Map() });
  }

  // ========== Group Methods ==========

  /**
   * Gets all groups.
   */
  getGroups(): WorkspaceGroup[] {
    return Array.from(this.groups.values());
  }

  /**
   * Gets group by ID.
   */
  getGroup(id: string): WorkspaceGroup | undefined {
    return this.groups.get(id);
  }

  /**
   * Gets the group that contains the given atom, if any.
   */
  getGroupForAtom(atomId: string): WorkspaceGroup | undefined {
    for (const group of this.groups.values()) {
      if (group.atomIds.has(atomId)) return group;
    }
    return undefined;
  }

  /**
   * Creates a new workspace with the group added.
   * Validates that atom IDs exist and aren't already in another group.
   *
   * @returns Object with new workspace and the generated group ID
   * @throws Error if any atom ID doesn't exist or is already grouped
   */
  addGroup(input: GroupInput): { workspace: MolecularWorkspace; id: string } {
    // Validate atoms exist
    for (const atomId of input.atomIds) {
      if (!this.atoms.has(atomId)) {
        throw new Error(`Atom ${atomId} not found in workspace`);
      }
    }

    // Validate no atom is already in another group
    for (const atomId of input.atomIds) {
      const existing = this.getGroupForAtom(atomId);
      if (existing) {
        throw new Error(`Atom ${atomId} is already in group "${existing.name}" (${existing.id})`);
      }
    }

    const id = input.id ?? `gr${stableId()}`;

    const newGroup: WorkspaceGroup = {
      id,
      name: input.name,
      templateId: input.templateId,
      atomIds: new Set(input.atomIds),
    };

    const newGroups = new Map(this.groups);
    newGroups.set(id, newGroup);

    const workspace = this._derive({ groups: newGroups });
    return { workspace, id };
  }

  /**
   * Creates a new workspace with the group removed.
   * Atoms and bonds remain — only the group association is removed.
   */
  removeGroup(id: string): MolecularWorkspace {
    if (!this.groups.has(id)) {
      return this;
    }

    const newGroups = new Map(this.groups);
    newGroups.delete(id);

    return this._derive({ groups: newGroups });
  }

  /**
   * Merges two or more groups into a single new group.
   * Original groups are dissolved. The new group contains the union of all atom IDs.
   *
   * @param groupIds - IDs of groups to merge
   * @param name - Name for the merged group
   * @returns Object with new workspace and the merged group ID
   * @throws Error if any group ID doesn't exist
   */
  mergeGroups(groupIds: string[], name: string): { workspace: MolecularWorkspace; id: string } {
    const mergedAtomIds: string[] = [];
    for (const gid of groupIds) {
      const group = this.groups.get(gid);
      if (!group) {
        throw new Error(`Group ${gid} not found`);
      }
      for (const atomId of group.atomIds) {
        mergedAtomIds.push(atomId);
      }
    }

    // Remove old groups
    const newGroups = new Map(this.groups);
    for (const gid of groupIds) {
      newGroups.delete(gid);
    }

    // Add merged group
    const id = `gr${stableId()}`;
    const merged: WorkspaceGroup = {
      id,
      name,
      atomIds: new Set(mergedAtomIds),
    };
    newGroups.set(id, merged);

    const workspace = this._derive({ groups: newGroups });
    return { workspace, id };
  }

  /**
   * Creates a new workspace with all groups cleared.
   */
  clearGroups(): MolecularWorkspace {
    if (this.groups.size === 0) {
      return this;
    }

    return this._derive({ groups: new Map() });
  }

  /**
   * Find all atoms, bonds, lone pairs/radicals, and charges in the connected component containing the given atom.
   *
   * Uses breadth-first search to traverse bonds and collect all reachable atoms and bonds.
   * Only traverses through visible atoms (skips bonds to hidden atoms).
   * Also collects lone pair, radical, and charge IDs for all visited atoms.
   *
   * @param atomId - The atom ID to start the search from
   * @returns Object with arrays of connected atomIds, bondIds, lonePairIds, and chargeIds
   * @throws Error if atomId doesn't exist in workspace
   *
   * @example
   * ```typescript
   * // For a molecule like: H-C-C-O+ (where O has 2 lone pairs and a positive charge)
   * const result = workspace.getConnectedElements('atom-2'); // carbon atom
   * // result = {
   * //   atomIds: ['atom-1', 'atom-2', 'atom-3', 'atom-4'],
   * //   bondIds: ['bond-1', 'bond-2', 'bond-3'],
   * //   lonePairIds: ['atom-4-lp0', 'atom-4-lp1'], // O has 2 lone pairs
   * //   chargeIds: ['atom-4-charge'] // O has a charge
   * // }
   * ```
   */
  getConnectedElements(atomId: string): { atomIds: string[], bondIds: string[], lonePairIds: string[], chargeIds: string[] } {
    // Validate input
    const startAtom = this.getAtom(atomId);
    if (!startAtom) {
      throw new Error(`Atom '${atomId}' not found in workspace`);
    }

    // Check if start atom is hidden (edge case)
    if (this.getVisibility(atomId) === VisibilityFlag.Hidden) {
      return { atomIds: [atomId], bondIds: [], lonePairIds: [], chargeIds: [] };
    }

    // BFS data structures
    const visitedAtoms = new Set<string>();
    const visitedBonds = new Set<string>();
    const queue: string[] = [atomId];

    // Traverse connected component
    while (queue.length > 0) {
      const currentAtomId = queue.shift()!;

      // Skip if already visited
      if (visitedAtoms.has(currentAtomId)) continue;
      visitedAtoms.add(currentAtomId);

      // Find all bonds involving this atom
      for (const [bondId, bond] of this.bonds) {
        let neighborId: string | null = null;

        // Check if this bond connects to current atom
        if (bond.a1.id === currentAtomId) {
          neighborId = bond.a2.id;
        } else if (bond.a2.id === currentAtomId) {
          neighborId = bond.a1.id;
        }

        // If bond is connected and not visited
        if (neighborId && !visitedBonds.has(bondId)) {
          // Check neighbor visibility
          const neighborVis = this.getVisibility(neighborId);
          if (neighborVis !== VisibilityFlag.Hidden) {
            visitedBonds.add(bondId);

            // Add neighbor to queue if not visited
            if (!visitedAtoms.has(neighborId)) {
              queue.push(neighborId);
            }
          }
        }
      }
    }

    // Generate lone pair, radical, and charge IDs for all visited atoms
    const lonePairIds: string[] = [];
    const chargeIds: string[] = [];
    for (const currentAtomId of visitedAtoms) {
      const atom = this.getAtom(currentAtomId);
      if (!atom) continue;

      // Generate IDs for lone pairs (using same pattern as HitboxLonePair)
      // Each atom can have multiple lone pairs and radicals
      const totalElectrons = atom.lonePairs + atom.radicals;
      for (let i = 0; i < totalElectrons; i++) {
        lonePairIds.push(`${currentAtomId}-lp${i}`);
      }

      // Generate charge ID if atom has a charge (using same pattern as DynamicCharge)
      if (atom.charge !== 0) {
        chargeIds.push(`${currentAtomId}-charge`);
      }
    }

    return {
      atomIds: Array.from(visitedAtoms),
      bondIds: Array.from(visitedBonds),
      lonePairIds,
      chargeIds
    };
  }

  /**
   * Looks up any element in the workspace by its ID.
   *
   * Returns a discriminated union so callers can identify element type
   * without relying on ID string conventions.
   *
   * Lookup order: atom → bond → hydrogen bond → lone pair → charge.
   * Lone pairs and charges are derived from their parent atom's properties.
   *
   * @param id - Element ID (e.g., "atVb3RQ", "btVb3RQ", "atVb3RQ-lp0", "atVb3RQ-charge")
   * @returns WorkspaceElement if found, undefined otherwise
   */
  getElement(id: string): WorkspaceElement | undefined {
    // Primary entities — O(1) Map lookups
    const atom = this.atoms.get(id);
    if (atom) return { type: 'atom', atom };

    const bond = this.bonds.get(id);
    if (bond) return { type: 'bond', bond };

    const hBond = this.hydrogenBonds.get(id);
    if (hBond) return { type: 'hydrogenbond', hydrogenBond: hBond };

    const annotation = this.annotations.get(id);
    if (annotation) return { type: 'annotation', annotation };

    const group = this.groups.get(id);
    if (group) return { type: 'group', group };

    // Derived entities — parse parent atom ID from suffix pattern
    const lpMatch = id.match(/^(.+)-lp(\d+)$/);
    if (lpMatch) {
      const parentAtom = this.atoms.get(lpMatch[1]);
      if (!parentAtom) return undefined;
      const index = parseInt(lpMatch[2], 10);
      // Validate the lone pair index actually exists
      const totalElectrons = parentAtom.lonePairs + parentAtom.radicals;
      if (index < 0 || index >= totalElectrons) return undefined;
      return { type: 'lonepair', atom: parentAtom, index };
    }

    const chargeMatch = id.match(/^(.+)-charge$/);
    if (chargeMatch) {
      const parentAtom = this.atoms.get(chargeMatch[1]);
      if (!parentAtom) return undefined;
      // Validate the atom actually has a charge
      if (parentAtom.charge === 0) return undefined;
      return { type: 'charge', atom: parentAtom };
    }

    return undefined;
  }

  /**
   * Checks if this workspace has the same arrows as another workspace.
   * Arrows are considered the same if they have matching encoded definitions,
   * regardless of order.
   *
   * @param other - Workspace to compare arrows with
   * @returns true if both workspaces have the same set of arrows
   *
   * @example
   * ```typescript
   * const ws1 = workspace.setArrows([{ type: 'pa', src: 0, dst: 1 }]);
   * const ws2 = workspace.setArrows([{ type: 'pa', src: 0, dst: 1 }]);
   * const ws3 = workspace.setArrows([{ type: 'pb', src: 2, dst: 3 }]);
   *
   * ws1.hasSameArrows(ws2); // true
   * ws1.hasSameArrows(ws3); // false
   * ```
   */
  hasSameArrows(other: MolecularWorkspace): boolean {
    // Quick length check
    if (this.arrows.length !== other.arrows.length) {
      return false;
    }

    // If both empty, they're the same
    if (this.arrows.length === 0) {
      return true;
    }

    // Compare ArrowIdDef directly using ID-based string keys
    const encodeIdArrow = (a: ArrowIdDef) =>
      `${a.type}:${a.srcId}:${a.dstId}:${a.anchorId ?? ''}`;
    const thisEncodedArrows = new Set(this.arrows.map(encodeIdArrow));
    const otherEncodedArrows = new Set(other.arrows.map(encodeIdArrow));

    // Check if sets are equal (same size and all elements match)
    if (thisEncodedArrows.size !== otherEncodedArrows.size) {
      return false;
    }

    for (const encoded of thisEncodedArrows) {
      if (!otherEncodedArrows.has(encoded)) {
        return false;
      }
    }

    return true;
  }

  // ========== Mutation Methods (T017-T020, T025) ==========

  /**
   * Adds atom to workspace with automatically generated sequential ID.
   * Returns new workspace (immutable API).
   *
   * @param atomData - Atom properties (ID will be auto-generated)
   * @returns Object with new workspace and the generated atom ID
   */
  addAtom(atomData: Omit<Atom, 'id'>, id?: string): { workspace: MolecularWorkspace; atomId: string } {
    const newId = id ?? `a${stableId()}`;
    const newAtom: Atom = { ...atomData, id: newId };

    const newAtoms = new Map(this.atoms);
    newAtoms.set(newId, newAtom);

    const workspace = this._derive({ atoms: newAtoms });
    return { workspace, atomId: newId };
  }

  /**
   * Batch-add atoms, bonds, and visibility in a single workspace mutation.
   * Much faster than chaining individual addAtom/addBond calls for bulk operations.
   *
   * Bonds reference atoms by their index in the `atoms` array (the newly created atoms).
   *
   * @returns New workspace + generated atom and bond IDs (in input order)
   */
  addAtomsAndBonds(input: {
    atoms: Omit<Atom, 'id'>[];
    bonds: Array<{ data: Omit<Bond, 'id' | 'a1' | 'a2'>; a1Index: number; a2Index: number }>;
    visibility?: Array<{ atomIndex: number; flag: VisibilityFlag }>;
  }): { workspace: MolecularWorkspace; atomIds: string[]; bondIds: string[] } {
    // Copy existing maps once
    const newAtoms = new Map(this.atoms);
    const newBonds = new Map(this.bonds);
    const newVisibility = new Map(this.visibility);

    // Add all atoms
    const atomIds: string[] = [];
    const atomObjects: Atom[] = [];
    for (const atomData of input.atoms) {
      const id = `a${stableId()}`;
      const atom: Atom = { ...atomData, id };
      newAtoms.set(id, atom);
      atomIds.push(id);
      atomObjects.push(atom);
    }

    // Add all bonds (reference atoms by index into the newly created atoms)
    const bondIds: string[] = [];
    for (const bondInput of input.bonds) {
      const a1 = atomObjects[bondInput.a1Index];
      const a2 = atomObjects[bondInput.a2Index];
      if (!a1 || !a2) continue;

      const id = `b${stableId()}`;
      const bond: Bond = { ...bondInput.data, id, a1, a2 };
      newBonds.set(id, bond);
      bondIds.push(id);
    }

    // Apply visibility
    if (input.visibility) {
      for (const vis of input.visibility) {
        const atomId = atomIds[vis.atomIndex];
        if (atomId) {
          newVisibility.set(atomId, vis.flag);
        }
      }
    }

    const workspace = this._derive({
      atoms: newAtoms,
      bonds: newBonds,
      visibility: newVisibility,
    });
    return { workspace, atomIds, bondIds };
  }

  /**
   * Removes atom from workspace.
   * Also cascades to remove all bonds referencing this atom.
   * Returns new workspace (immutable API).
   *
   * @param atomId - ID of atom to remove
   * @returns New workspace with atom and dependent bonds removed
   */
  removeAtom(atomId: string): MolecularWorkspace {
    if (!this.atoms.has(atomId)) {
      return this; // Atom doesn't exist, return unchanged
    }

    const newAtoms = new Map(this.atoms);
    newAtoms.delete(atomId);

    // Cascade delete: remove all bonds referencing this atom
    const newBonds = new Map<string, Bond>();

    for (const [bondId, bond] of this.bonds) {
      if (bond.a1.id !== atomId && bond.a2.id !== atomId) {
        newBonds.set(bondId, bond);
      }
    }

    // Remove visibility setting for deleted atom
    const newVisibility = new Map(this.visibility);
    newVisibility.delete(atomId);

    // Also cascade delete: remove hydrogen bonds referencing this atom
    const newHydrogenBonds = new Map<string, HydrogenBond>();
    for (const [hbId, hb] of this.hydrogenBonds) {
      if (hb.donorAtomId !== atomId && hb.donorParentAtomId !== atomId && hb.acceptorAtomId !== atomId) {
        newHydrogenBonds.set(hbId, hb);
      }
    }

    // Cascade: remove atom from any group it belongs to
    const newGroups = new Map(this.groups);
    for (const [gid, group] of this.groups) {
      if (group.atomIds.has(atomId)) {
        const newAtomIds = new Set(group.atomIds);
        newAtomIds.delete(atomId);
        if (newAtomIds.size === 0) {
          newGroups.delete(gid); // Empty group — remove entirely
        } else {
          newGroups.set(gid, { ...group, atomIds: newAtomIds });
        }
      }
    }

    return this._derive({
      atoms: newAtoms,
      bonds: newBonds,
      visibility: newVisibility,
      hydrogenBonds: newHydrogenBonds,
      groups: newGroups,
    });
  }

  /**
   * Adds bond between two atoms.
   * Returns new workspace (immutable API).
   *
   * @param bondData - Bond properties (ID and atom references will be set automatically)
   * @param a1Id - ID of first atom
   * @param a2Id - ID of second atom
   * @returns Object with new workspace and the generated bond ID
   * @throws Error if either atom doesn't exist
   */
  addBond(bondData: Omit<Bond, 'id' | 'a1' | 'a2'>, a1Id: string, a2Id: string, id?: string): { workspace: MolecularWorkspace; bondId: string } {
    // Use getAtom() to resolve both loose IDs and compound IDs (instance atoms)
    const a1 = this.getAtom(a1Id);
    const a2 = this.getAtom(a2Id);

    if (!a1) {
      throw new Error(`Atom '${a1Id}' not found in workspace`);
    }
    if (!a2) {
      throw new Error(`Atom '${a2Id}' not found in workspace`);
    }
    if (a1Id === a2Id) {
      throw new Error(`Cannot create self-bond: atom '${a1Id}' to itself`);
    }

    const newId = id ?? `b${stableId()}`;
    const newBond: Bond = { ...bondData, id: newId, a1, a2 };

    const newBonds = new Map(this.bonds);
    newBonds.set(newId, newBond);

    const workspace = this._derive({ bonds: newBonds });
    return { workspace, bondId: newId };
  }

  /**
   * Removes bond from workspace.
   * Returns new workspace (immutable API).
   *
   * @param bondId - ID of bond to remove
   * @returns New workspace with bond removed
   */
  removeBond(bondId: string): MolecularWorkspace {
    if (!this.bonds.has(bondId)) {
      return this; // Bond doesn't exist, return unchanged
    }

    const newBonds = new Map(this.bonds);
    newBonds.delete(bondId);

    return this._derive({ bonds: newBonds });
  }

  /**
   * Sets visibility flag for an atom.
   * Returns new workspace (immutable API).
   *
   * @param atomId - ID of atom
   * @param flag - Visibility flag to set
   * @returns New workspace with visibility updated
   */
  setVisibility(atomId: string, flag: VisibilityFlag): this {
    const newVisibility = new Map(this.visibility);
    newVisibility.set(atomId, flag);

    return this._derive({ visibility: newVisibility }) as this;
  }

  /**
   * Toggles Hidden H visibility for hydrogen atoms bonded to specified atoms.
   * For each selected atom, finds all bonded hydrogen atoms and toggles their visibility.
   * Toggles between Visible and Hidden.
   *
   * @param atomIds - Array of atom IDs whose bonded hydrogens should be toggled
   * @returns New workspace with toggled hydrogen visibility
   *
   * @example
   * ```ts
   * // Toggle H atoms bonded to selected atoms
   * const selection = ['atom-1', 'atom-2']; // Carbons with bonded H atoms
   * workspace = workspace.toggleHiddenH(selection);
   * ```
   */
  toggleHiddenH(atomIds: string[]): this {
    let result = this;

    // Collect all hydrogen atoms bonded to selected atoms
    const hydrogenIds = new Set<string>();

    for (const atomId of atomIds) {
      const atom = this.atoms.get(atomId);
      if (!atom) continue;

      // If the selected atom itself is hydrogen, toggle it
      if (atom.symbol === 'H') {
        hydrogenIds.add(atomId);
      } else {
        // Find all bonded hydrogen atoms
        for (const bond of this.bonds.values()) {
          let hydrogenId: string | null = null;

          if (bond.a1.id === atomId) {
            if (bond.a2.symbol === 'H') {
              hydrogenId = bond.a2.id;
            }
          } else if (bond.a2.id === atomId) {
            if (bond.a1.symbol === 'H') {
              hydrogenId = bond.a1.id;
            }
          }

          if (hydrogenId) {
            hydrogenIds.add(hydrogenId);
          }
        }
      }
    }

    // Determine visibility setting for all hydrogen atoms
    let isHidden = false;
    for (const hydrogenId of hydrogenIds) {
      const currentVisibility = result.getVisibility(hydrogenId);
      if (currentVisibility === VisibilityFlag.Hidden) {
        isHidden = true;
        break;
      }
    }

    // Toggle all collected hydrogen atoms
    for (const hydrogenId of hydrogenIds) {
      result = result.setVisibility(hydrogenId, isHidden ? VisibilityFlag.Visible : VisibilityFlag.Hidden);
    }

    return result;
  }

  /**
   * Toggles lone pair visibility for specified atoms.
   * Toggles between Visible and HiddenLP.
   * Atoms currently Hidden are not affected.
   *
   * @param atomIds - Array of atom IDs to toggle
   * @returns New workspace with toggled lone pair visibility
   *
   * @example
   * ```ts
   * // Toggle lone pairs for selected atoms
   * const selection = ['atom-1', 'atom-2'];
   * workspace = workspace.toggleLonePairs(selection);
   * ```
   */
  toggleLonePairs(atomIds: string[]): this {
    let result = this;

    for (const atomId of atomIds) {
      const atom = this.atoms.get(atomId);
      if (!atom) {
        continue; // Skip invalid atom IDs
      }

      const currentVisibility = this.getVisibility(atomId);

      // Don't toggle if atom is completely Hidden
      if (currentVisibility === VisibilityFlag.Hidden) {
        continue;
      }

      // Toggle lone pair visibility while preserving ForceText state
      // Visible ↔ HiddenLP
      // ForceText ↔ ForceTextHiddenLP
      let newVisibility: VisibilityFlag;
      if (currentVisibility === VisibilityFlag.Visible) {
        newVisibility = VisibilityFlag.HiddenLP;
      } else if (currentVisibility === VisibilityFlag.HiddenLP) {
        newVisibility = VisibilityFlag.Visible;
      } else if (currentVisibility === VisibilityFlag.ForceText) {
        newVisibility = VisibilityFlag.ForceTextHiddenLP;
      } else if (currentVisibility === VisibilityFlag.ForceTextHiddenLP) {
        newVisibility = VisibilityFlag.ForceText;
      } else {
        continue;
      }

      result = result.setVisibility(atomId, newVisibility);
    }

    return result;
  }

  /**
   * Toggles ForceText flag for selected carbon atoms.
   * Forces carbon atoms to render as text instead of paper carbons.
   * Returns new workspace (immutable API).
   *
   * @param atomIds - Array of atom IDs to toggle
   * @returns New workspace with ForceText toggled
   *
   * @example
   * ```ts
   * // Toggle ForceText for selected carbon atoms
   * const selection = ['atom-1', 'atom-2']; // Carbon atoms
   * workspace = workspace.toggleForceText(selection);
   * ```
   */
  toggleForceText(atomIds: string[]): this {
    let result = this;

    for (const atomId of atomIds) {
      const atom = this.atoms.get(atomId);
      if (!atom) {
        continue; // Skip invalid atom IDs
      }

      // Only apply ForceText to carbon atoms
      if (atom.symbol !== 'C') {
        continue;
      }

      const currentVisibility = this.getVisibility(atomId);

      // Don't toggle if atom is Hidden
      if (currentVisibility === VisibilityFlag.Hidden) {
        continue;
      }

      // Toggle ForceText flag while preserving lone pair visibility
      // Visible ↔ ForceText
      // HiddenLP ↔ ForceTextHiddenLP
      let newVisibility: VisibilityFlag;
      if (currentVisibility === VisibilityFlag.Visible) {
        newVisibility = VisibilityFlag.ForceText;
      } else if (currentVisibility === VisibilityFlag.ForceText) {
        newVisibility = VisibilityFlag.Visible;
      } else if (currentVisibility === VisibilityFlag.HiddenLP) {
        newVisibility = VisibilityFlag.ForceTextHiddenLP;
      } else if (currentVisibility === VisibilityFlag.ForceTextHiddenLP) {
        newVisibility = VisibilityFlag.HiddenLP;
      } else {
        continue;
      }

      result = result.setVisibility(atomId, newVisibility);
    }

    return result;
  }

  /**
   * Updates atom position.
   * Returns new workspace (immutable API).
   *
   * @param atomId - ID of atom to update
   * @param x - New x coordinate
   * @param y - New y coordinate
   * @returns New workspace with atom position updated
   */
  updateAtomPosition(atomId: string, x: number, y: number): MolecularWorkspace {
    // Delegates to the plural form, which is copy-on-write. This method used to
    // assign `atom.x = x` in place, on the reasoning that bonds hold object
    // references so never breaking the reference keeps them correct. It does —
    // but it also writes through every other holder of that object. See
    // PIPH-336: `Template.workspace` shares its atom objects with every
    // instance stamped from it, and `useUndoRedo` clones the previous present
    // *at push time*, i.e. after the mutation, so undo restored the edited
    // value. Two callers already worked around it by calling `clone()` first.
    return this.updateAtomPositions(new Map([[atomId, { x, y }]]));
  }

  /**
   * Updates the lone pair count for a single atom.
   * Returns new workspace (immutable API).
   *
   * @param atomId - ID of atom to update
   * @param lonePairs - New lone pair count
   * @returns New workspace with updated lone pairs
   */
  updateAtomLonePairs(atomId: string, lonePairs: number): MolecularWorkspace {
    // Copy-on-write via `_replaceAtom`; see the note on `updateAtomPosition`
    // and PIPH-336 for why the previous in-place assignment was a bug.
    return this._replaceAtom(atomId, { lonePairs });
  }

  /**
   * Updates multiple atom positions in a single operation.
   * More efficient than calling updateAtomPosition multiple times.
   *
   * @param positions - Map of atomId to {x, y} coordinates
   * @returns New workspace with all atom positions updated
   *
   * @example
   * ```ts
   * const positions = new Map([
   *   ['atom-1', { x: 10, y: 20 }],
   *   ['atom-2', { x: 30, y: 40 }]
   * ]);
   * const newWorkspace = workspace.updateAtomPositions(positions);
   * ```
   */
  updateAtomPositions(positions: Map<string, { x: number; y: number }>): MolecularWorkspace {
    // Shallow copy the atoms Map (same atom references initially)
    const newAtoms = new Map(this.atoms);
    const movedAtomIds = new Set<string>();

    // Create new atom objects only for atoms that actually moved
    // This preserves immutability while being efficient (O(m) where m = moved atoms)
    positions.forEach((pos, atomId) => {
      const atom = this.atoms.get(atomId);
      if (atom) {
        // Only create new object if position actually changed
        if (atom.x !== pos.x || atom.y !== pos.y) {
          // Create new atom object with updated position (spread preserves all other properties)
          newAtoms.set(atomId, { ...atom, x: pos.x, y: pos.y });
          movedAtomIds.add(atomId);
        }
      }
    });

    // If no changes, return same instance
    if (movedAtomIds.size === 0) {
      return this;
    }

    // Update bonds that reference moved atoms
    // Bonds hold direct object references, so we need new bond objects
    // pointing to the new atom objects
    const newBonds = new Map(this.bonds);
    this.bonds.forEach((bond, bondId) => {
      const a1Moved = movedAtomIds.has(bond.a1.id);
      const a2Moved = movedAtomIds.has(bond.a2.id);

      if (a1Moved || a2Moved) {
        // Create new bond with references to the new atom objects
        newBonds.set(bondId, {
          ...bond,
          a1: a1Moved ? newAtoms.get(bond.a1.id)! : bond.a1,
          a2: a2Moved ? newAtoms.get(bond.a2.id)! : bond.a2,
        });
      }
    });

    // Return new workspace instance with new atoms and bonds Maps
    return this._derive({
      atoms: newAtoms,
      bonds: newBonds,
    });
  }

  // ========== Property Mutation (PIPH-335) ==========

  /**
   * Replaces properties on one or more atoms, copy-on-write.
   *
   * The engine behind every atom-property setter, and the only place the
   * bond-rebuild is written. `Bond.a1`/`a2` are **object references** to
   * `Atom` (see models/Bond.ts), so a new atom object requires a new bond
   * object for every bond touching it — otherwise bonds keep pointing at the
   * pre-edit atom and readers that go through the object path (rendering's
   * `buildStructure` reads `bond.a1.symbol`, as does `toggleHiddenH` here)
   * silently see stale chemistry.
   *
   * Deliberately NOT the in-place approach of `updateAtomPosition` /
   * `updateAtomLonePairs`, which assign onto the shared `Atom`. That preserves
   * bond references by never breaking them, but it aliases into any `Template`
   * or history snapshot sharing that object — see PIPH-336, which reimplements
   * both over this helper.
   *
   * Loose atoms only. A compound ID naming an atom inside a *collapsed*
   * instance returns `this` untouched: an instance is a read-only view of its
   * template, and editing its contents requires exploding it first.
   */
  private _replaceAtoms(
    patches: ReadonlyMap<string, AtomPropertyPatch>,
  ): MolecularWorkspace {
    const newAtoms = new Map(this.atoms);
    const changedAtomIds = new Set<string>();

    patches.forEach((patch, atomId) => {
      const atom = this.atoms.get(atomId);
      if (!atom) return;

      // Only rewrite an atom whose values actually differ. This is what keeps a
      // controlled field from pushing an undo entry for a value it re-sent
      // unchanged, and it preserves object identity for React memoization.
      let changed = false;
      for (const key of Object.keys(patch) as (keyof AtomPropertyPatch)[]) {
        const next = patch[key];
        if (next !== undefined && atom[key] !== next) {
          changed = true;
          break;
        }
      }
      if (!changed) return;

      newAtoms.set(atomId, { ...atom, ...patch, id: atomId });
      changedAtomIds.add(atomId);
    });

    if (changedAtomIds.size === 0) {
      return this;
    }

    const newBonds = new Map(this.bonds);
    this.bonds.forEach((bond, bondId) => {
      const a1Changed = changedAtomIds.has(bond.a1.id);
      const a2Changed = changedAtomIds.has(bond.a2.id);
      if (a1Changed || a2Changed) {
        newBonds.set(bondId, {
          ...bond,
          a1: a1Changed ? newAtoms.get(bond.a1.id)! : bond.a1,
          a2: a2Changed ? newAtoms.get(bond.a2.id)! : bond.a2,
        });
      }
    });

    return this._derive({ atoms: newAtoms, bonds: newBonds });
  }

  /** Single-atom form of `_replaceAtoms`, so there is one bond-rebuild. */
  private _replaceAtom(atomId: string, patch: AtomPropertyPatch): MolecularWorkspace {
    return this._replaceAtoms(new Map([[atomId, patch]]));
  }

  /**
   * Replaces properties on one bond, copy-on-write.
   *
   * Simpler than the atom case: nothing in the model holds a reference to a
   * `Bond`, only to its id, so no rebuild cascades from here. Spreading the
   * existing bond is what preserves the `a1`/`a2` references — they must not
   * be re-resolved.
   */
  private _replaceBond(bondId: string, patch: BondPropertyPatch): MolecularWorkspace {
    const bond = this.bonds.get(bondId);
    if (!bond) {
      return this;
    }

    let changed = false;
    for (const key of Object.keys(patch) as (keyof BondPropertyPatch)[]) {
      const next = patch[key];
      if (next !== undefined && bond[key] !== next) {
        changed = true;
        break;
      }
    }
    if (!changed) {
      return this;
    }

    const newBonds = new Map(this.bonds);
    newBonds.set(bondId, { ...bond, ...patch, id: bondId, a1: bond.a1, a2: bond.a2 });
    return this._derive({ bonds: newBonds });
  }

  /**
   * Changes an atom's element.
   *
   * Nothing else changes — not lone pairs, not charge, not the bonds already
   * on the atom. PIPH Build is a documentation app: the user says what the
   * structure is, and checking whether it is sensible chemistry is their job,
   * not the model's.
   *
   * @param atomId - ID of the atom to change
   * @param symbol - New element symbol
   * @returns New workspace, or `this` if unknown or unchanged
   */
  setAtomSymbol(atomId: string, symbol: string): MolecularWorkspace {
    return this._replaceAtom(atomId, { symbol });
  }

  /**
   * Sets an atom's formal charge.
   *
   * User-set and stored verbatim; lone pairs are left alone. Charge is the
   * dependent variable in the formal-charge relation, so holding bonds and
   * radicals fixed a ±1 charge step has no lone-pair solution — any attempt to
   * "keep it consistent" would have to move lone pairs by a half, silently
   * change the bonding, or refuse odd steps.
   *
   * @param atomId - ID of the atom to charge
   * @param charge - New formal charge
   * @returns New workspace, or `this` if unknown or unchanged
   */
  setAtomCharge(atomId: string, charge: number): MolecularWorkspace {
    return this._replaceAtom(atomId, { charge });
  }

  /**
   * Sets an atom's unpaired-electron count.
   *
   * @param atomId - ID of the atom to change
   * @param radicals - New radical count
   * @returns New workspace, or `this` if unknown or unchanged
   */
  setAtomRadicals(atomId: string, radicals: number): MolecularWorkspace {
    return this._replaceAtom(atomId, { radicals });
  }

  /**
   * Changes several of one atom's properties in a single derive.
   *
   * Prefer this over chaining setters when more than one property moves
   * together: chained calls rebuild every touching bond once each and expose
   * intermediate workspaces that no user action produced.
   *
   * @param atomId - ID of the atom to change
   * @param patch - Properties to change; omitted keys are left alone
   * @returns New workspace, or `this` if unknown or unchanged
   */
  updateAtomProperties(atomId: string, patch: AtomPropertyPatch): MolecularWorkspace {
    return this._replaceAtom(atomId, patch);
  }

  /**
   * Changes properties on many atoms in a single derive.
   *
   * The multi-select path. Chaining N single calls would copy the bonds Map N
   * times and — once wired to history — push N undo entries for what the user
   * experienced as one action.
   *
   * @param patches - Map of atom ID to the properties to change
   * @returns New workspace, or `this` if nothing changed
   */
  updateAtomsProperties(
    patches: ReadonlyMap<string, AtomPropertyPatch>,
  ): MolecularWorkspace {
    return this._replaceAtoms(patches);
  }

  /**
   * Sets a bond's order, preserving its ID.
   *
   * The ID is the point. The established idiom for changing an order is
   * `removeBond` then `addBond`, which mints a *new* ID unless the old one is
   * passed back explicitly — and the two call sites doing it in the demo app
   * both forget to, so promoting a single bond to a double silently breaks
   * anything keyed on the bond: selection, arrow retargeting, and the
   * connective-bond list an expanded group instance rebuilds itself from.
   *
   * @param bondId - ID of the bond to change
   * @param order - 1, 2, or 3
   * @throws RangeError if `order` is not 1, 2, or 3
   * @returns New workspace, or `this` if unknown or unchanged
   */
  setBondOrder(bondId: string, order: number): MolecularWorkspace {
    if (!Number.isInteger(order) || order < 1 || order > 3) {
      throw new RangeError(`Bond order must be 1, 2, or 3 (received ${order})`);
    }
    return this._replaceBond(bondId, { order });
  }

  /**
   * Sets how a bond is drawn (l=line, d=dashed, w=wedge, s=squiggle).
   *
   * @param bondId - ID of the bond to change
   * @param bondType - New bond type
   * @throws RangeError if `bondType` is not one of the four
   * @returns New workspace, or `this` if unknown or unchanged
   */
  setBondType(bondId: string, bondType: Bond['bondType']): MolecularWorkspace {
    if (bondType !== 'l' && bondType !== 'd' && bondType !== 'w' && bondType !== 's') {
      throw new RangeError(`Bond type must be 'l', 'd', 'w' or 's' (received ${bondType})`);
    }
    return this._replaceBond(bondId, { bondType });
  }

  /**
   * Changes several of one bond's properties in a single derive.
   *
   * @param bondId - ID of the bond to change
   * @param patch - Properties to change; omitted keys are left alone
   * @returns New workspace, or `this` if unknown or unchanged
   */
  updateBondProperties(bondId: string, patch: BondPropertyPatch): MolecularWorkspace {
    if (patch.order !== undefined && (!Number.isInteger(patch.order) || patch.order < 1 || patch.order > 3)) {
      throw new RangeError(`Bond order must be 1, 2, or 3 (received ${patch.order})`);
    }
    return this._replaceBond(bondId, patch);
  }

  /**
   * Reverses a bond's direction, keeping its ID.
   *
   * Not cosmetic: a wedge or dash is drawn from the narrow end at `a1` to the
   * wide end at `a2`, so endpoint order *is* the stereo direction. Without
   * this a wedge can only ever point one way, and the only workaround is
   * remove-then-re-add, which loses the ID.
   *
   * @param bondId - ID of the bond to reverse
   * @returns New workspace, or `this` if the bond does not exist
   */
  flipBond(bondId: string): MolecularWorkspace {
    const bond = this.bonds.get(bondId);
    if (!bond) {
      return this;
    }
    const newBonds = new Map(this.bonds);
    newBonds.set(bondId, { ...bond, a1: bond.a2, a2: bond.a1 });
    return this._derive({ bonds: newBonds });
  }

  /**
   * Sets arrow definitions using ID-based arrows.
   * Returns new workspace (immutable API).
   *
   * Arrows are stored as IDs for stability across workspace transformations.
   * Conversion to indices happens only during serialization or pushing.
   *
   * @param arrows - Array of arrow ID definitions
   * @returns New workspace with arrows updated
   */
  setArrows(arrows: ArrowIdDef[]): MolecularWorkspace {
    // Store arrows with IDs for stability
    return this._derive({ arrows: arrows });
  }

  /**
   * Adds arrow using element IDs.
   * Stores IDs directly for stability across workspace transformations.
   * Returns new workspace (immutable API).
   *
   * @param type - Arrow type (pa, pb, ba, bb)
   * @param sourceId - Source atom or bond ID
   * @param targetId - Target atom or bond ID
   * @param anchorAtomId - Anchor atom ID (required for ba/bb types)
   * @returns New workspace with arrow added
   *
   * @example
   * ```typescript
   * // Add pa arrow from atom-1 to atom-2
   * const updated = workspace.addArrowFromIds('pa', 'atom-1', 'atom-2');
   *
   * // Add ba arrow with anchor atom
   * const updated = workspace.addArrowFromIds('ba', 'bond-1', 'atom-3', 'atom-1');
   * ```
   */
  addArrowFromIds(
    type: 'pa' | 'pb' | 'ba' | 'bb',
    sourceId: string,
    targetId: string,
    anchorAtomId?: string
  ): MolecularWorkspace {
    // Validate that source exists (in either atoms or bonds)
    const srcInAtoms = this.atoms.has(sourceId);
    const srcInBonds = this.bonds.has(sourceId);
    if (!srcInAtoms && !srcInBonds) {
      throw new Error(`Source ID not found: ${sourceId}`);
    }

    // Validate that target exists (in either atoms or bonds)
    const dstInAtoms = this.atoms.has(targetId);
    const dstInBonds = this.bonds.has(targetId);
    if (!dstInAtoms && !dstInBonds) {
      throw new Error(`Target ID not found: ${targetId}`);
    }

    // Create ArrowIdDef - store IDs directly, not indices
    const newArrow: ArrowIdDef = {
      type: type as any, // ArrowType enum values match string literals
      srcId: sourceId,
      dstId: targetId
    };

    // Add anchor atom ID for ba/bb types
    if (anchorAtomId && (type === 'ba' || type === 'bb')) {
      // Validate anchor exists in atoms
      if (!this.atoms.has(anchorAtomId)) {
        throw new Error(`Anchor atom ID not found: ${anchorAtomId}`);
      }
      newArrow.anchorId = anchorAtomId;
    }

    // Add to arrows array
    return this.setArrows([...this.arrows, newArrow]);
  }

  /**
   * Reveal (pop out) any Hidden atoms involved in an electron-movement arrow so
   * the subsequent bond op does not operate on a hidden atom (MC-868).
   *
   * sourceId / targetId may each be an atom ID or a bond ID; for a bond, both
   * endpoint atoms are revealed. anchorAtomId (if given) is always an atom ID.
   * Only atoms currently Hidden are flipped to Visible; HiddenLP / ForceText /
   * ForceTextHiddenLP are left untouched (already visible, modified rendering).
   * Immutable: returns a new workspace, or `this` if nothing was hidden.
   *
   * @param sourceId - Source atom or bond ID
   * @param targetId - Target atom or bond ID
   * @param anchorAtomId - Optional anchor atom ID (ba/bb types)
   * @returns New workspace with involved hidden atoms revealed, or `this`
   */
  revealAtomsForArrow(sourceId: string, targetId: string, anchorAtomId?: string): this {
    const atomIds = new Set<string>();
    const expand = (id?: string) => {
      if (!id) return;
      if (this.atoms.has(id)) {
        atomIds.add(id);
        return;
      }
      const bond = this.getBond(id);
      if (bond) {
        atomIds.add(bond.a1.id);
        atomIds.add(bond.a2.id);
      }
    };
    expand(sourceId);
    expand(targetId);
    expand(anchorAtomId);

    let ws = this as MolecularWorkspace;
    for (const id of atomIds) {
      if (ws.getVisibility(id) === VisibilityFlag.Hidden) {
        ws = ws.setVisibility(id, VisibilityFlag.Visible);
      }
    }
    return ws as this;
  }

  // ========== Bond Lookup Utilities (for Arrow Remapping) ==========

  /**
   * Find a bond by the atoms it connects.
   * Useful for stable bond references that don't rely on bond IDs.
   *
   * @param atomId1 - First atom ID
   * @param atomId2 - Second atom ID
   * @returns Bond if found, null otherwise
   *
   * @example
   * ```typescript
   * const bond = workspace.findBondByAtoms('atom-1', 'atom-2');
   * if (bond) {
   *   // console.log(`Bond order: ${bond.order}`);
   * }
   * ```
   */
  findBondByAtoms(atomId1: string, atomId2: string): Bond | null {
    for (const bond of this.bonds.values()) {
      if (
        (bond.a1.id === atomId1 && bond.a2.id === atomId2) ||
        (bond.a1.id === atomId2 && bond.a2.id === atomId1)
      ) {
        return bond;
      }
    }
    return null;
  }

  /**
   * Get the atom pair for a bond (stable bond reference).
   * Returns atom IDs in normalized order (smaller ID first).
   *
   * @param bondId - Bond ID
   * @returns Tuple of [atomId1, atomId2] or null if bond not found
   *
   * @example
   * ```typescript
   * const [atom1, atom2] = workspace.getBondAtomPair('bond-5');
   * // Can now track this bond across transformations using atom pair
   * ```
   */
  getBondAtomPair(bondId: string): [string, string] | null {
    const bond = this.bonds.get(bondId);
    if (!bond) return null;

    // Normalize: return smaller ID first for consistent references
    const [id1, id2] =
      bond.a1.id < bond.a2.id ? [bond.a1.id, bond.a2.id] : [bond.a2.id, bond.a1.id];
    return [id1, id2];
  }

  /**
   * Deep clone the workspace for undo/redo.
   * Creates new instances of all atoms, bonds, and collections to ensure
   * React detects changes via object identity.
   *
   * Critical for undo/redo: Even though workspace is immutable, it shares
   * underlying atom/bond objects. Cloning ensures each history entry has
   * independent objects that React can differentiate.
   *
   * @returns New workspace with deeply cloned atoms, bonds, and collections
   * @example
   * ```typescript
   * const original = workspace.addAtom({ symbol: 'C', x: 0, y: 0, ... });
   * const cloned = original.clone();
   *
   * // Different workspace instance
   * expect(cloned).not.toBe(original);
   *
   * // Different atom objects (React can detect changes)
   * expect(cloned.getAtom('atom-1')).not.toBe(original.getAtom('atom-1'));
   *
   * // But same data
   * expect(cloned.getAtom('atom-1')).toEqual(original.getAtom('atom-1'));
   * ```
   */
  clone(): MolecularWorkspace {
    // Deep clone atoms (new object for each atom)
    const clonedAtoms = new Map<string, Atom>();
    this.atoms.forEach((atom, id) => {
      clonedAtoms.set(id, { ...atom });
    });

    // Deep clone bonds with updated atom references
    // CRITICAL: Bond.a1 and Bond.a2 must reference the CLONED atoms, not originals
    const clonedBonds = new Map<string, Bond>();
    this.bonds.forEach((bond, id) => {
      const a1 = clonedAtoms.get(bond.a1.id);
      const a2 = clonedAtoms.get(bond.a2.id);

      if (!a1 || !a2) {
        // console.warn(`clone(): Bond ${id} references non-existent atoms`);
        return;
      }

      clonedBonds.set(id, {
        ...bond,
        a1,
        a2,
      });
    });

    // Clone collections (arrays and maps)
    const clonedVisibility = new Map(this.visibility);
    const clonedArrows = [...this.arrows];
    const clonedHydrogenBonds = new Map(this.hydrogenBonds);
    const clonedAnnotations = new Map(this.annotations);
    const clonedGroups = new Map(this.groups);

    return this._derive({
      atoms: clonedAtoms,
      bonds: clonedBonds,
      visibility: clonedVisibility,
      arrows: clonedArrows,
      hydrogenBonds: clonedHydrogenBonds,
      annotations: clonedAnnotations,
      groups: clonedGroups,
    });
  }

  // ========== Compound-ID Resolution (PIPH-77) ==========

  /**
   * Resolve a compound ID to an atom with world-space coordinates.
   * Parses the compound ID, looks up the instance and template,
   * then applies the instance's transform to the template-local position.
   */
  private _resolveInstanceAtom(compoundId: string): Atom | undefined {
    const { groupId, localId } = parseCompoundId(compoundId);
    if (!groupId) return undefined;

    const instance = this.groupInstances.get(groupId);
    if (!instance) return undefined;

    const template = this.templates.get(instance.templateId);
    if (!template) return undefined;

    const localAtom = template.workspace.getAtom(localId);
    if (!localAtom) return undefined;

    // Apply per-instance overrides (sparse — fallback to template values)
    const ov = instance.overrides;
    const symbol = ov?.elements?.get(localId) ?? localAtom.symbol;
    const charge = ov?.charges?.get(localId) ?? localAtom.charge;
    const lonePairs = ov?.lonePairs?.get(localId) ?? localAtom.lonePairs;
    const radicals = ov?.radicals?.get(localId) ?? localAtom.radicals;

    // Apply instance transform: rotate then translate
    const { tx, ty, rotation } = instance.transform;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const worldX = localAtom.x * cos - localAtom.y * sin + tx;
    const worldY = localAtom.x * sin + localAtom.y * cos + ty;

    return {
      ...localAtom,
      id: compoundId,
      symbol,
      charge,
      lonePairs,
      radicals,
      x: worldX,
      y: worldY,
    };
  }

  /**
   * Resolve a compound ID to a bond with world-space endpoints.
   */
  private _resolveInstanceBond(compoundId: string): Bond | undefined {
    const { groupId, localId } = parseCompoundId(compoundId);
    if (!groupId) return undefined;

    const instance = this.groupInstances.get(groupId);
    if (!instance) return undefined;

    const template = this.templates.get(instance.templateId);
    if (!template) return undefined;

    const localBond = template.workspace.getBond(localId);
    if (!localBond) return undefined;

    // Resolve both endpoints with transform applied
    const a1 = this._resolveInstanceAtom(makeCompoundId(groupId, localBond.a1.id));
    const a2 = this._resolveInstanceAtom(makeCompoundId(groupId, localBond.a2.id));
    if (!a1 || !a2) return undefined;

    return {
      ...localBond,
      id: compoundId,
      a1,
      a2,
    };
  }

  // ========== Loose-Atom-Only Accessors (PIPH-77) ==========

  /** Returns only loose atom IDs (not from instances). */
  getLooseAtomIds(): string[] {
    return Array.from(this.atoms.keys());
  }

  /** Returns only loose atoms (not from instances). */
  getAllLooseAtoms(): Atom[] {
    return Array.from(this.atoms.values());
  }

  /** Returns only loose atom count (not from instances). */
  getLooseAtomCount(): number {
    return this.atoms.size;
  }

  /** Returns only loose bond IDs (not from instances). */
  getLooseBondIds(): string[] {
    return Array.from(this.bonds.keys());
  }

  /** Returns only loose bonds (not from instances). */
  getAllLooseBonds(): Bond[] {
    return Array.from(this.bonds.values());
  }

  /** Returns only loose bond count (not from instances). */
  getLooseBondCount(): number {
    return this.bonds.size;
  }

  // ========== Template Methods (PIPH-77) ==========

  addTemplate(template: Template): MolecularWorkspace {
    const newTemplates = new Map(this.templates);
    newTemplates.set(template.id, template);
    return this._derive({ templates: newTemplates });
  }

  removeTemplate(id: TemplateId): MolecularWorkspace {
    if (!this.templates.has(id)) return this;
    const newTemplates = new Map(this.templates);
    newTemplates.delete(id);
    return this._derive({ templates: newTemplates });
  }

  getTemplate(id: TemplateId): Template | undefined {
    return this.templates.get(id);
  }

  getTemplates(): Template[] {
    return Array.from(this.templates.values());
  }

  getTemplateCount(): number {
    return this.templates.size;
  }

  // ========== GroupInstance Methods (PIPH-77) ==========

  addGroupInstance(
    templateId: TemplateId,
    transform: Transform2D,
    overrides?: InstanceOverrides,
    instanceId?: GroupId,
  ): { workspace: MolecularWorkspace; instanceId: GroupId } {
    if (!this.templates.has(templateId)) {
      throw new Error(`Template "${templateId}" not found`);
    }
    const id = instanceId ?? (`gi${stableId()}` as GroupId);
    const instance: GroupInstance = { id, templateId, transform, overrides };
    const newInstances = new Map(this.groupInstances);
    newInstances.set(id, instance);
    return { workspace: this._derive({ groupInstances: newInstances }), instanceId: id };
  }

  removeGroupInstance(instanceId: GroupId): MolecularWorkspace {
    if (!this.groupInstances.has(instanceId)) return this;
    const newInstances = new Map(this.groupInstances);
    newInstances.delete(instanceId);
    return this._derive({ groupInstances: newInstances });
  }

  /**
   * Repositions a collapsed instance, bringing any bonds attached to it along.
   *
   * The second half is not optional (PIPH-339). A bond addressing an atom
   * inside a collapsed instance stores what `getAtom` returned for that
   * compound ID — and `_resolveInstanceAtom` returns a **snapshot with world
   * coordinates already baked in**, because bonds hold object references
   * rather than ids. Moving the instance by writing `transform` alone
   * therefore left every such bond drawn at the position the instance used to
   * be: visibly detached from the structure it connects to.
   *
   * That is a core interaction rather than an edge case wherever a template
   * instance is treated as a read-only view — bonding *to* one is not a change
   * to its template, so it stays collapsed and stays draggable.
   *
   * @param instanceId - ID of the instance to move
   * @param transform - New transform
   * @returns New workspace, or `this` if the instance does not exist
   */
  moveGroupInstance(instanceId: GroupId, transform: Transform2D): MolecularWorkspace {
    const existing = this.groupInstances.get(instanceId);
    if (!existing) return this;
    const updated: GroupInstance = { ...existing, transform };
    const newInstances = new Map(this.groupInstances);
    newInstances.set(instanceId, updated);
    const moved = this._derive({ groupInstances: newInstances });

    // Re-resolve endpoints against the new transform. Built lazily so a move
    // with nothing attached does not copy the bonds Map at all.
    let newBonds: Map<string, Bond> | undefined;
    this.bonds.forEach((bond, bondId) => {
      const a1Belongs = parseCompoundId(bond.a1.id).groupId === instanceId;
      const a2Belongs = parseCompoundId(bond.a2.id).groupId === instanceId;
      if (!a1Belongs && !a2Belongs) return;

      const a1 = a1Belongs ? moved.getAtom(bond.a1.id) : bond.a1;
      const a2 = a2Belongs ? moved.getAtom(bond.a2.id) : bond.a2;
      if (!a1 || !a2) return;

      newBonds ??= new Map(this.bonds);
      newBonds.set(bondId, { ...bond, a1, a2 });
    });

    return newBonds ? moved._derive({ bonds: newBonds }) : moved;
  }

  getGroupInstance(id: GroupId): GroupInstance | undefined {
    return this.groupInstances.get(id);
  }

  getGroupInstances(): GroupInstance[] {
    return Array.from(this.groupInstances.values());
  }

  getGroupInstanceCount(): number {
    return this.groupInstances.size;
  }

}
